import { loadScriptPromise } from "../../utils/script.ts";
import chameleonjs from "chameleonjs";
import { once } from "../../utils/function.ts";
import { decodeHTML } from "../../utils/string.ts";

interface BrandSchema {
  Bridge: {
    bridgeBrand: string;
    enabled: boolean;
    storeSearchEnable: boolean;
  };
  jigsawHost?: string;
}

type SuppressedSearch<T extends VST.Bridge.SearchContent> = Pick<
  T,
  "search" | "content" | "totalElements" | "totalPages" | "page"
>;

let bridgeBrandData: BrandSchema["Bridge"] | undefined = undefined;
let partialBridgeUser: Pick<VST.Bridge.User, "hasAssets" | "branding"> &
  Pick<Partial<VST.Bridge.User>, "hasAdmin" | "hasGroups" | "promotions"> = {
  hasAssets: false,
  branding: {},
};

/**
 * The one case where explore on/off status can change is if the user redeems
 * a code for explore content when they didn't have any before. The redeem
 * triggers a parallel user + list update as is normal for react-router, but
 * the list update will be blocked by the current hasAssets: false state.
 * When a redeem occurs, pretend like the user has assets so we can fetch any
 * needed details, but then reset it to the actual value when the user load
 * completes
 */
export function forceExploreRevalidation() {
  partialBridgeUser = { hasAssets: true, branding: partialBridgeUser.branding };
}

function decodeAssetFields<T extends VST.Bridge.Asset>(asset: T): T {
  return {
    ...asset,
    title: decodeHTML(asset.title),
    description: asset.description && decodeHTML(asset.description),
  };
}

const setUpBridgeIfNeeded = once(async () => {
  const [_boxResponse, _bridgeResponse, _exploreResponse, brandResponse] =
    await Promise.all([
      loadScriptPromise(
        `https://${import.meta.env.VITE_JIGSAW_HOST}/assets/v1/jigsaw/box.js`
      ),
      loadScriptPromise(
        `https://${import.meta.env.VITE_BRIDGE_HOST}/assets/v1/bridge.js`
      ),
      loadScriptPromise(
        `https://${import.meta.env.VITE_EXPLORE_HOST}/assets/v1/explore.js`
      ),
      // The brand use in the API is not ideal and I'd rather not pollute the dewey schema
      // with this data at this time
      chameleonjs.loadBrand<BrandSchema>(window.location.hostname, "fraction"),
    ]);

  // Set custom jigsaw host for custom hostnames
  if (brandResponse.jigsawHost && window.Jigsaw) {
    window.Jigsaw.host = brandResponse.jigsawHost;
  }

  bridgeBrandData = brandResponse.Bridge;

  // This must be called once before any other bridge calls
  try {
    partialBridgeUser = await window.VST.Bridge.Session.get(getDefaultOpts());
  } catch {
    // Stick with the has no explore default
  }
});

function exploreSupported() {
  return !!(bridgeBrandData?.enabled && partialBridgeUser?.hasAssets);
}

function getDefaultOpts() {
  const opts: Record<string, string> = {};
  if (bridgeBrandData?.bridgeBrand) {
    opts.brand = bridgeBrandData.bridgeBrand;
  }
  return opts;
}

async function callBridge<T>(
  fn: (opts: Record<string, string>) => Promise<T>,
  suppressedResponse?: T
) {
  await setUpBridgeIfNeeded();
  if (exploreSupported() || !suppressedResponse) {
    return fn(getDefaultOpts());
  }
  return Promise.resolve(suppressedResponse);
}

export function getBridgeUser() {
  // This will ultimately double-call Session.get, but I don't want to change it due
  // to possible the one-time init with possible parallel API calls
  return callBridge(async (opts) => {
    const result = await window.VST.Bridge.Session.get(opts);
    partialBridgeUser = result;
    return result;
  });
}

export function getRecentAssets(listOptions: { limit?: number } = {}) {
  return callBridge(
    (opts) =>
      window.VST.Bridge.Assets.lists({
        lists: [
          {
            sort: "recent",
            active: true,
            ...listOptions,
          },
        ],
        ...opts,
      }),
    { status: 200, lists: [], message: "", timestamp: "" }
  );
}

export function getAsset(vbid: string) {
  return callBridge((opts) =>
    window.VST.Bridge.Assets.get({
      vbid,
      ...opts,
    })
  );
}

export function returnAsset(vbid: string) {
  return callBridge((opts) =>
    window.VST.Bridge.Assets.return({ vbid, ...opts })
  );
}

export function searchExplore(search: string, page?: number, size?: number) {
  const emptyResult = {
    search,
    content: [],
    totalElements: 0,
    totalPages: 0,
    page: 0,
  } as SuppressedSearch<VST.Bridge.SearchContent>;
  return callBridge(
    (opts) =>
      window.VST.Bridge.Assets.list({
        search,
        active: false,
        ...(!!page && { page }),
        ...(!!size && { size }),
        ...opts,
      }),
    emptyResult
  )
    .then((result) => ({
      ...result,
      content: result.content.map(decodeAssetFields),
    }))
    .catch(() => emptyResult);
}

export function activateAsset(vbid: string, credit: string) {
  return callBridge((opts) =>
    window.VST.Bridge.Assets.activate({
      vbid,
      credit,
      ...opts,
    })
  );
}

export function getAssetsByVbid(vbids: string[]) {
  return callBridge((opts) =>
    window.VST.Bridge.Assets.list({
      vbids: vbids.join(","),
      ...opts,
    }).catch(
      (error: {
        status: number;
        error: string | undefined;
        message: string;
        content: VST.Bridge.Asset[];
      }) => {
        return {
          status: error.status,
          content: [],
          message: error.message,
        };
      }
    )
  );
}

export function activatePromotion(vbid: string) {
  return callBridge((opts) =>
    window.VST.Bridge.Promotions.activate({
      vbid,
      active: true,
      ...opts,
    })
  );
}

export function returnPromotion(vbid: string) {
  return callBridge((opts) =>
    window.VST.Bridge.Promotions.return({
      vbid,
      active: false,
      ...opts,
    })
  );
}

interface LaneConfig {
  sort: string;
  filters: string[];
}

const defaultLanes: LaneConfig[] = [
  { sort: "all", filters: [] },
  { sort: "recommended", filters: [] },
  { sort: "popular", filters: [] },
  { sort: "added", filters: [] },
];

const defaultSubjectLanes: LaneConfig[] = [
  { sort: "popular", filters: ["recommended.subject1"] },
  { sort: "popular", filters: ["recommended.subject2"] },
  { sort: "popular", filters: ["recommended.subject3"] },
];

function getListConfig() {
  const lists = [...defaultLanes];
  lists.push(
    ...Object.entries(partialBridgeUser.branding).map(([key]) => ({
      sort: "popular",
      filters: [`subscription.${key}`],
    }))
  );
  lists.push(...defaultSubjectLanes);

  return lists;
}

function getListConfigForSub(subscriptionId: string) {
  const lists = [...defaultLanes];

  const brandParams = partialBridgeUser.branding[subscriptionId];
  if (brandParams?.lanes) {
    lists.push(
      ...brandParams.lanes.map((laneName) => ({
        sort: "popular",
        filters: [`subject.${laneName}`],
      }))
    );
  } else {
    lists.push(...defaultSubjectLanes);
  }

  return lists.map((list) => ({
    ...list,
    filters: [...list.filters, `subscription.${subscriptionId}`],
  }));
}

/**
 * This is heavier than typical for these types of method call files. The main
 * reason the call details are built up here rather than in the loader is because
 * the subscription branding affects the fetched data, and that information
 * is not available in loader functions. The main alternative would be to initiate
 * the fetch in the UI, but the explore user is already deferred and that will
 * push out that fetch even further.
 */
export function getBaseLists(subscriptionId?: string) {
  return callBridge(async (opts) => {
    const lists = subscriptionId
      ? getListConfigForSub(subscriptionId)
      : getListConfig();

    if (subscriptionId) {
      const promotion = partialBridgeUser.promotions
        ?.filter((promo) => promo.type === "FREE_SUBSCRIPTION")
        .find((promo) => promo.value === subscriptionId);

      if (promotion && !promotion.active) {
        await activatePromotion(promotion.value);
      }
    }

    return window.VST.Bridge.Assets.lists({
      lists: lists.map((listData) => ({
        ...listData,
        limit: 100,
      })),
      ...opts,
    });
  });
}

export function getAncillaries(vbid: string) {
  return callBridge((opts) => {
    return window.VST.Bridge.Ancillaries.get({ ...opts, vbid });
  });
}

type AncillaryMap = Record<string, VST.Bridge.Ancillary[]>;

export function getAncillariesForVbids(vbids: string[]): Promise<AncillaryMap> {
  return Promise.all(
    vbids.map((vbid) =>
      getAncillaries(vbid).catch(() => ({ title: "", ancillaries: [] }))
    )
  ).then((responses) =>
    responses.reduce((ancillaryMap, response, index) => {
      const vbid = vbids[index];
      ancillaryMap[vbid] = response.ancillaries;
      return ancillaryMap;
    }, {} as AncillaryMap)
  );
}
